import { Link } from "react-router-dom";

import { AppCard, AppCardBody, AppText, kindredFontFamilies } from "../../ui-foundation";
import {
  DocumentList,
  DocumentPageScaffold,
  DocumentSection,
} from "@/components/DocumentScaffold";
import {
  KINDRED_COUNTRY_ANNEX_TERMS_BULLETS,
  KINDRED_NO_SALE_OR_AD_MARKETING_SHARE_COPY,
  KINDRED_SUPPORT_COUNTRY_COVERAGE_COPY,
  KINDRED_SUPPORT_EMAIL,
  KINDRED_SERVICE_PROVIDER_DISCLOSURE_COPY,
  KINDRED_TERMS_GOVERNING_LAW_COPY,
  STEADY_HOUSE_LEGAL_NAME,
  STEADY_HOUSE_SUPPORT_URL,
} from "../../shared/support";

const Terms = () => {
  return (
    <DocumentPageScaffold
      title="Terms & Conditions"
      updatedLabel="Last updated: April 2026"
      fallbackPath="/"
    >
      <DocumentSection title="1. Acceptance Of Terms">
        <AppText>
          By accessing and using Kindred, you agree to these Terms &amp; Conditions. If you do not agree, do not
          use the app.
        </AppText>
      </DocumentSection>

      <DocumentSection title="2. Eligibility">
        <AppText>
          You must be at least 18 years old to use Kindred. The service is designed for single parents to
          connect, plan activities, and find support.
        </AppText>
      </DocumentSection>

      <DocumentSection title="3. Your Account">
        <AppText>To use member features, you need a Kindred account. You agree to:</AppText>
        <DocumentList
          items={[
            "Provide accurate information",
            "Keep your login details secure",
            "Tell us if you believe someone has accessed your account without permission",
            "Take responsibility for activity carried out through your account",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="4. Community Standards">
        <AppText>You must not use Kindred to:</AppText>
        <DocumentList
          items={[
            "Harass, abuse, or endanger other members",
            "Post misleading, fraudulent, or unlawful content",
            "Share material you do not have the right to share",
            "Attempt unauthorised access to Kindred systems or data",
            "Use the service for commercial activity without permission",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="5. User Content">
        <AppText>
          You keep ownership of the content you post, but you give Kindred a non-exclusive licence to host,
          display, and distribute that content inside the service so the product can work as intended.
        </AppText>
        <DocumentList
          items={[
            "You may only upload photos, videos, or other material that you have the right to share.",
            "If a child is identifiable in content you upload, you must have the authority or permission needed to share that image.",
            "You must remove content if Kindred asks you to do so for privacy, safety, or legal reasons.",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="6. Privacy And Data">
        <AppText>
          Kindred&apos;s{" "}
          <Link to="/privacy" className="kindred-inline-link">
            Privacy Policy
          </Link>{" "}
          explains what personal data we collect, how we use it, and how you can access, correct, or erase
          that data.
        </AppText>
      </DocumentSection>

      <DocumentSection title="7. Information And Support Boundaries">
        <AppText>
          Kindred is a community product. It is not a law firm, medical service, emergency response provider,
          regulated financial adviser, or government body.
        </AppText>
        <AppText>{KINDRED_SUPPORT_COUNTRY_COVERAGE_COPY}</AppText>
      </DocumentSection>

      <DocumentSection title="8. Intellectual Property">
        <AppText>
          Kindred, its branding, and its original product content are owned or licensed by {STEADY_HOUSE_LEGAL_NAME}
          {" "}unless stated otherwise. You may not copy, modify, distribute, or reverse engineer the service
          without permission.
        </AppText>
      </DocumentSection>

      <DocumentSection title="9. Disclaimer Of Warranties">
        <AppText>
          Kindred is provided on an &quot;as is&quot; basis. We do not promise uninterrupted availability, perfect
          accuracy, or that the service will always be error-free.
        </AppText>
      </DocumentSection>

      <DocumentSection title="10. Limitation Of Liability">
        <AppText>
          To the maximum extent permitted by law, Kindred is not liable for indirect, incidental, special, or
          consequential loss arising from use of the app.
        </AppText>
      </DocumentSection>

      <DocumentSection title="11. Third-Party Services">
        <AppText>
          Kindred may link to third-party websites or depend on third-party service providers. We are not
          responsible for the content or practices of third-party sites.
        </AppText>
        <AppText>{KINDRED_SERVICE_PROVIDER_DISCLOSURE_COPY}</AppText>
        <AppText>{KINDRED_NO_SALE_OR_AD_MARKETING_SHARE_COPY}</AppText>
      </DocumentSection>

      <DocumentSection title="12. Suspension And Termination">
        <AppText>
          We may suspend or terminate accounts that breach these Terms or create safety risks for the
          community. Members can also request account deletion through the app at any time.
        </AppText>
        <AppText>
          If Kindred takes content moderation or account action against a member, the member can ask for a
          review through the support route listed in these Terms and the Contact &amp; complaints page.
        </AppText>
      </DocumentSection>

      <DocumentSection title="13. Changes To These Terms">
        <AppText>
          We may update these Terms from time to time. Continued use of Kindred after a material update means
          you accept the revised Terms.
        </AppText>
      </DocumentSection>

      <DocumentSection title="14. Governing Law">
        <AppText>{KINDRED_TERMS_GOVERNING_LAW_COPY}</AppText>
      </DocumentSection>

      <DocumentSection title="15. Contact And Complaints">
        <AppText>
          For questions about these Terms, service complaints, or concerns about how Kindred guidance was
          presented, use the{" "}
          <a
            href={STEADY_HOUSE_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="kindred-inline-link"
          >
            Steady House support page
          </a>{" "}
          or email{" "}
          <a href={`mailto:${KINDRED_SUPPORT_EMAIL}`} className="kindred-inline-link">
            {KINDRED_SUPPORT_EMAIL}
          </a>
          .
        </AppText>
      </DocumentSection>

      <DocumentSection title="16. Country Annexes">
        <DocumentList items={[...KINDRED_COUNTRY_ANNEX_TERMS_BULLETS]} />
      </DocumentSection>

      <AppCard backgroundColor="$panelBackground">
        <AppCardBody alignItems="center">
          <AppText
            textAlign="center"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize="$md"
            lineHeight="$md"
          >
            These Terms work alongside the Privacy Policy, community rules, child-safety rules, and the
            support-guidance limits that keep Kindred clear and safe for members.
          </AppText>
        </AppCardBody>
      </AppCard>
    </DocumentPageScaffold>
  );
};

export default Terms;
